import { StudentCreditsResponse } from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { DialogContent } from "components/Dialog";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatCurrency = (value: number | undefined | null) =>
  currencyFormatter.format((value ?? 0) / 100);

export const getStudentCreditsTotal = (student: StudentCreditsResponse) => {
  const generalCredits = student.general_credits ?? 0;
  const subjectCredits =
    student.subject_credits?.reduce(
      (sum, subject) => sum + (subject.credits ?? 0),
      0
    ) ?? 0;

  return generalCredits + subjectCredits;
};

export const getStudentCreditsBreakdown = (student: StudentCreditsResponse) => {
  const generalCredits = student.general_credits ?? 0;
  const subjectCredits =
    student.subject_credits?.reduce(
      (sum, subject) => sum + (subject.credits ?? 0),
      0
    ) ?? 0;

  const parts = [
    generalCredits,
    ...(student.subject_credits?.map((subject) => subject.credits ?? 0) ?? []),
  ];

  const total = generalCredits + subjectCredits;
  const positive = parts
    .filter((value) => value > 0)
    .reduce((a, b) => a + b, 0);
  const negative = parts
    .filter((value) => value < 0)
    .reduce((a, b) => a + b, 0);

  return { total, positive, negative };
};

type CreditDetailsProps = {
  credits: StudentCreditsResponse[];
};

export default function CreditDetails({ credits }: CreditDetailsProps) {
  const hasCredits = credits.length > 0;
  const totalPositiveCredits = credits.reduce(
    (sum, student) => sum + getStudentCreditsBreakdown(student).positive,
    0
  );
  const totalNegativeCredits = credits.reduce(
    (sum, student) => sum + getStudentCreditsBreakdown(student).negative,
    0
  );

  return (
    <DialogContent className="balance-details-dialog-content credit-breakdown-dialog">
      <div className="credit-breakdown-header">
        <div>
          <div className="credit-breakdown-title">Student Credit Breakdown</div>
          <div className="credit-breakdown-subtitle">
            {hasCredits
              ? `Showing ${credits.length} ${
                  credits.length === 1 ? "student" : "students"
                }.`
              : "No outstanding student credits."}
            {hasCredits && (
              <div>
                <div>
                  Prepaid Tutoring (Services Owed to Students):{" "}
                  <span className="font-semibold">
                    {formatCurrency(totalPositiveCredits)}
                  </span>
                </div>
                <div>
                  Unpaid Tutoring (Payment Owed by Students):{" "}
                  <span className="font-semibold">
                    {formatCurrency(totalNegativeCredits)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {hasCredits ? (
        <div className="credit-details-table-container">
          <table className="credit-details-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Total Positive Credits (Prepaid Tutoring)</th>
                <th>Total Negative Credits (Unpaid Tutoring)</th>
                <th>Net Credits</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {credits.map((student) => {
                const { total, positive, negative } =
                  getStudentCreditsBreakdown(student);

                return (
                  <tr key={student.student_id}>
                    <td>
                      <div>
                        {student.student_first_name} {student.student_last_name}
                      </div>
                    </td>
                    <td>{formatCurrency(positive)}</td>
                    <td>{formatCurrency(negative)}</td>
                    <td>{formatCurrency(total)}</td>
                    <td>
                      <Button
                        color={ButtonColor.DARK_BLUE}
                        size={ButtonSize.EXTRA_SMALL}
                        onClick={() =>
                          window.open(
                            `/manage/students/${student.student_id}#billing`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        View Student
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="credit-details-empty text-center">
          There are currently no credits owed to students at this time.
        </div>
      )}
    </DialogContent>
  );
}
